import { useState, useEffect, useCallback, useRef } from 'react';
import { getTariffs } from '../services/tariffsApi';

/**
 * Fetches tariffs list from API. Returns { data, loading, error, refetch }.
 * @returns {{ data: import('../config/tariffs').TariffPlan[] | null, loading: boolean, error: Error | null, refetch: () => Promise<void> }}
 */
export function useTariffs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const fetchTariffs = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getTariffs();
      if (!mountedRef.current) return;
      setData(Array.isArray(list) ? list : []);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setData(null);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchTariffs();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchTariffs]);

  return { data, loading, error, refetch: fetchTariffs };
}
